// Customer notes rotator: crossfades paired items (image + quote) that share a data-note-item index.
// - Auto-advances every data-interval ms when data-autoplay is "true".
// - Pauses on hover, keyboard focus inside, when off screen, and for prefers-reduced-motion.
// - Prev/next buttons use commands: commandfor="<id>" command="--prev" | "--next".
// - In the theme editor, selecting a note's block shows that note and stops rotation.
class SNoteRotator extends HTMLElement {
  #controller = null;
  #timer = 0;
  #index = 0;
  #count = 0;
  #hovered = false;
  #focused = false;
  #visible = false;
  #locked = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const indexes = [...this.querySelectorAll('[data-note-item]')].map((el) => Number(el.dataset.noteItem));
    this.#count = indexes.length ? Math.max(...indexes) + 1 : 0;
    if (this.#count < 2) return;

    this.#show(0);

    this.addEventListener('command', (event) => {
      if (event.command === '--next') this.#step(1);
      else if (event.command === '--prev') this.#step(-1);
    }, { signal });

    this.addEventListener('pointerenter', () => { this.#hovered = true; this.#schedule(); }, { signal });
    this.addEventListener('pointerleave', () => { this.#hovered = false; this.#schedule(); }, { signal });
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focused = false; this.#schedule(); }
    }, { signal });

    this.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest?.('[data-note-item]');
      if (!item) return;
      this.#locked = true;
      this.#show(Number(item.dataset.noteItem));
      this.#schedule();
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => { this.#locked = false; this.#schedule(); }, { signal });

    const observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#schedule();
    }, { threshold: 0.25 });
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', () => this.#schedule(), { signal });
  }

  #reducedMotion = null;

  #step(delta) {
    this.#show((this.#index + delta + this.#count) % this.#count);
    this.#schedule();
  }

  #show(index) {
    this.#index = index;
    for (const el of this.querySelectorAll('[data-note-item]')) {
      const active = Number(el.dataset.noteItem) === index;
      el.toggleAttribute('data-active', active);
      el.toggleAttribute('inert', !active);
      if (active) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', 'true');
    }
  }

  #schedule() {
    clearTimeout(this.#timer);
    const autoplay = this.dataset.autoplay === 'true';
    const paused = this.#hovered || this.#focused || !this.#visible || this.#locked || this.#reducedMotion?.matches;
    if (!autoplay || paused) return;
    const interval = Math.max(3000, Number(this.dataset.interval) || 6000);
    this.#timer = setTimeout(() => this.#step(1), interval);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }
}

if (!customElements.get('s-note-rotator')) customElements.define('s-note-rotator', SNoteRotator);
